import styled, { keyframes } from 'styled-components';

import { media } from 'src/styles/breakpoints';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

export const StyledModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: clamp(0.75rem, 3vw, 1.5rem);
  padding-top: max(clamp(0.75rem, 3vw, 1.5rem), env(safe-area-inset-top, 0px));
  padding-bottom: max(clamp(0.75rem, 3vw, 1.5rem), env(safe-area-inset-bottom, 0px));
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  background: ${({ theme }) => theme.modalBackground ?? 'rgba(15, 18, 28, 0.55)'};
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  animation: ${fadeIn} 0.22s ease-out;
`;

export const StyledModalContainer = styled.div<{ isOpen: boolean; loading?: boolean; $hasChildren?: boolean; $wide?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: ${({ $hasChildren, $wide }) => ($wide ? 'min(72rem, 100%)' : $hasChildren ? 'min(42rem, 100%)' : 'min(65rem, 100%)')};
  max-height: min(90dvh, calc(100dvh - 2 * clamp(0.75rem, 3vw, 1.5rem)));
  margin: auto 0;
  flex-shrink: 0;
  min-height: 0;
  z-index: 2;
  border-radius: 1rem;
  overflow: hidden;
  background: ${({ theme }) => theme.background};
  color: ${({ theme }) => theme.dark};
  border: 1px solid ${({ theme }) => theme.grey};
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.04) inset,
    0 24px 48px -12px rgba(15, 18, 28, 0.35),
    0 12px 24px -8px rgba(15, 18, 28, 0.2);
  animation: ${scaleIn} 0.28s cubic-bezier(0.22, 1, 0.36, 1);
`;

export const StyledModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
  padding: 0.5rem 0.5rem 0 0.75rem;
  border-bottom: 1px solid ${({ theme }) => theme.grey};
  background: ${({ theme }) => theme.lightGrey};
`;

export const StyledModalDismiss = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  margin: 0.25rem;
  padding: 0;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 1.35rem;
  line-height: 1;
  color: ${({ theme }) => theme.midGrey};
  background: transparent;
  transition:
    color 0.15s ease,
    background 0.15s ease;

  &:hover {
    color: ${({ theme }) => theme.dark};
    background: ${({ theme }) => theme.background};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.primary};
    outline-offset: 2px;
  }
`;

export const StyledModalBody = styled.div<{ $centered?: boolean }>`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: ${({ $centered }) => ($centered ? '2rem' : '1.25rem 1.5rem 1.5rem')};

  ${media.md} {
    padding: ${({ $centered }) => ($centered ? '1.5rem 1rem' : '1rem')};
  }
  display: ${({ $centered }) => ($centered ? 'flex' : 'block')};
  align-items: ${({ $centered }) => ($centered ? 'center' : 'stretch')};
  justify-content: ${({ $centered }) => ($centered ? 'center' : 'flex-start')};
`;

export const StyledModalFooter = styled.div`
  display: flex;
  flex-shrink: 0;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 1rem 1.5rem 1.25rem;

  ${media.md} {
    padding: 0.75rem 1rem 1rem;
    justify-content: stretch;

    button {
      flex: 1 1 auto;
      min-width: 0;
    }
  }
  border-top: 1px solid ${({ theme }) => theme.grey};
  background: ${({ theme }) => theme.lightGrey};
`;

export const StyledCloseButton = styled.span`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  cursor: pointer;
  font-size: 1.5rem;
`;

export const StyledButtonsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: center;
`;

export const StyledTextarea = styled.textarea`
  width: calc(100% - 50px);
  padding: 1.25rem;
  min-height: 6rem;
  resize: none;
  outline: none;
`;

export const StyledImg = styled.img`
  max-width: 20rem;
  max-height: 20rem;
`;

export const StyledWrap = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

export const StyledList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 3rem;
`;

export const StyledSelect = styled.select`
  padding: 0.5rem;
  margin-bottom: 1.25rem;
  outline: none;
`;
